'use client';

import { useForm, Controller } from 'react-hook-form';
import { useRef, useState } from 'react';
import TextField from '../Text-fild/Text-fild';
import TextFieldComment from '../Text-fild-comment/Text-fild-comment';
import ContactPhoneInput from '../ContactPhoneInput/ContactPhoneInput';

import styles from './contact-form.module.scss';
import { FormData } from '@/types/contactsBlock.types';
import { ErrorModal, SuccessModal } from '@/components';
import ButtonSimple from '@/components/HeroBlock/components/ButtonSimple/ButtonSimple';
import CheckboxForForm from '@/components/CheckboxForForm/CheckboxForForm';
import { getAttributionPayload } from '@/utils/getAttributionPayload';

const filterCyrillicFromEmail = (value: string) =>
  value.replace(/[а-яё]/gi, '');

const ContactForm = () => {
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<FormData>({
    defaultValues: {
      email: '',
      name: '',
      phone: '',
      company: '',
      comment: '',
    },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const onSubmit = async (data: FormData) => {
    try {
      const attribution = getAttributionPayload(formRef.current);
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...data, ...attribution }),
      });

      const result = await response.json();
      if (!response.ok)
        throw new Error(result.error || 'Failed to submit form');

      reset(undefined, {
        keepErrors: false,
        keepDirty: false,
        keepIsSubmitted: false,
        keepTouched: false,
        keepIsValid: false,
        keepSubmitCount: false,
      });
      setSubmitAttempted(false);
      setIsSuccess(true);
    } catch (error) {
      setIsError(true);
      console.error('Error submitting form:', error);
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitAttempted(true);
    handleSubmit(onSubmit)(e);
  };

  return (
    <>
      <form
        ref={formRef}
        className={styles.form}
        onSubmit={handleFormSubmit}
      >
        <Controller
          name="email"
          control={control}
          rules={{
            required: 'Email is required',
            pattern: { value: /\S+@\S+\.\S+/, message: 'Invalid email format' },
          }}
          render={({ field, fieldState }) => (
            <TextField
              label="Email"
              placeholder="Enter your email"
              value={field.value}
              onChange={(value) =>
                field.onChange(filterCyrillicFromEmail(value))
              }
              error={fieldState.error?.message}
            />
          )}
        />

        <Controller
          name="name"
          control={control}
          rules={{ required: 'Name is required' }}
          render={({ field, fieldState }) => (
            <TextField
              label="Your name"
              placeholder="Enter your name"
              value={field.value}
              onChange={field.onChange}
              error={fieldState.error?.message}
            />
          )}
        />

        <Controller
          name="phone"
          control={control}
          rules={{
            validate: (value) => {
              const v = (value || '').trim();
              if (!v || v === '+') return 'Please enter your phone number';
              const digits = v.replace(/\D/g, '');
              if (digits.length < 6)
                return 'Please enter a complete phone number';
              if (digits.length > 19) return 'Phone number is too long';
              return true;
            },
          }}
          render={({ field, fieldState }) => {
            const shouldShowError = submitAttempted && fieldState.error;
            return (
              <ContactPhoneInput
                label="Phone"
                placeholder="Enter your phone"
                value={field.value || ''}
                onChange={(phone) => {
                  field.onChange(phone);
                }}
                error={shouldShowError ? fieldState.error?.message : undefined}
              />
            );
          }}
        />

        <Controller
          name="company"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              label="Company name"
              placeholder="Enter your company name"
              value={field.value}
              onChange={field.onChange}
              error={fieldState.error?.message}
            />
          )}
        />

        <Controller
          name="comment"
          control={control}
          render={({ field, fieldState }) => (
            <TextFieldComment
              value={field.value}
              onChange={field.onChange}
              error={fieldState.error?.message}
            />
          )}
        />

        <CheckboxForForm
          consentChecked={consentChecked}
          setConsentChecked={setConsentChecked}
        />

        <div className={styles.buttonWrapper}>
          <ButtonSimple
            text={isSubmitting ? 'Sending...' : 'Send'}
            designBlendingMode=""
            designBlendingMode1=""
            type="submit"
            disabled={isSubmitting}
          />
        </div>
      </form>

      {isSuccess && <SuccessModal onClose={() => setIsSuccess(false)} />}
      {isError && <ErrorModal onClose={() => setIsError(false)} />}
    </>
  );
};

export default ContactForm;
